import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { CLIENT_EVENT, logClientEvent } from '@/app/lib/datadog/client-config';

export enum ScormLessonStatusEnum {
	NotAttempted = 'not attempted',
	Incomplete = 'incomplete',
	Completed = 'completed',
}
interface ScormContextInterface {
	initialLessonStatus: ScormLessonStatusEnum | null;
	scormUpadateLessonStatus: (value: string) => void;
}

export const ScormContext = React.createContext<ScormContextInterface>({
	initialLessonStatus: null,
	scormUpadateLessonStatus: () => null,
});

const SCORM_HANDSHAKE_TIMEOUT_MS = 5000;

export const SCORM_COMPLETION_UNREACHABLE =
	'This course could not be marked complete in your learning system. Your progress may not have been saved.';

// Per tab. Survives in-tab navigations. Set once the launch page has answered the handshake.
const SCORM_HOST_KEY = 'praxis.scormHost';

const writeScormHost = (write: (storage: Storage) => void): boolean => {
	try {
		write(sessionStorage);
		return true;
	} catch {
		return false;
	}
};

// Lasts for this document, including after completion. sessionStorage is cleared on completion so a reload does not keep the host.
let rememberedScormHost: 'opener' | 'parent' | null = null;

const rememberScormHost = (kind: 'opener' | 'parent') => {
	rememberedScormHost = kind;
	writeScormHost((storage) => storage.setItem(SCORM_HOST_KEY, kind));
};

const forgetScormHost = () => {
	writeScormHost((storage) => storage.removeItem(SCORM_HOST_KEY));
};

export const clearRememberedScormHost = () => {
	rememberedScormHost = null;
};

const expectedScormHost = () => {
	if (rememberedScormHost) return rememberedScormHost;
	try {
		return sessionStorage.getItem(SCORM_HOST_KEY);
	} catch {
		return null;
	}
};

export type ScormMessageTarget =
	| { status: 'ready'; target: Window; hasOpener: boolean }
	| { status: 'missing'; hasOpener: boolean };

// A scenario opened by the SCORM launch page has that page as window.opener.
// Older packages embed the scenario in an iframe, so the launch page is window.parent.
// A closed or severed opener is not a host. Posting to window itself would be ignored.
export const scormMessageTarget = (
	current: Window = window,
	expected: string | null = expectedScormHost(),
): ScormMessageTarget => {
	try {
		const { opener } = current;
		if (opener && opener !== current) {
			if (opener.closed) {
				return { status: 'missing', hasOpener: true };
			}
			return { status: 'ready', target: opener, hasOpener: true };
		}
	} catch {
		return { status: 'missing', hasOpener: true };
	}

	// Chrome sets window.opener to null once the launch page is gone. The handshake proved there was one.
	if (expected === 'opener') {
		return { status: 'missing', hasOpener: true };
	}

	if (current.parent && current.parent !== current) {
		return { status: 'ready', target: current.parent, hasOpener: false };
	}

	return { status: 'missing', hasOpener: false };
};

const reportUnreachableHost = (messageType: unknown, hasOpener: boolean) => {
	if (!hasOpener) return;
	logClientEvent(CLIENT_EVENT.SCORM_HOST_UNREACHABLE, { messageType, hasOpener }, 'warn').catch(() => undefined);
};

const postToScormHost = (message: Record<string, unknown>): ScormMessageTarget & { delivered: boolean } => {
	const result = scormMessageTarget();
	if (result.status === 'missing') {
		reportUnreachableHost(message.type, result.hasOpener);
		return { ...result, delivered: false };
	}
	result.target.postMessage(message, '*');
	return { ...result, delivered: true };
};

export default function ScormProvider({ children }: { children: ReactNode }) {
	const [initialLessonStatus, setInitialLessonStatus] = useState<ScormLessonStatusEnum | null>(null);
	const [isSCORM, setIsSCORM] = useState(false);
	const [completionUnreachable, setCompletionUnreachable] = useState(false);

	const updateSCORM = useCallback((value: string) => {
		const result = postToScormHost({ type: 'UPDATE_LESSON_STATUS', lessonStatus: value });
		if (result.delivered) {
			if (value === ScormLessonStatusEnum.Completed) {
				forgetScormHost();
			}
			setCompletionUnreachable(false);
			return;
		}
		if (result.hasOpener) {
			setCompletionUnreachable(true);
		}
	}, []);

	const scormContextValue = useMemo(
		() => ({
			initialLessonStatus,
			scormUpadateLessonStatus: updateSCORM,
		}),
		[initialLessonStatus, updateSCORM],
	);

	useEffect(() => {
		let handshakeTimer: number | undefined;

		const clearHandshakeTimer = () => {
			if (handshakeTimer === undefined) return;
			window.clearTimeout(handshakeTimer);
			handshakeTimer = undefined;
		};

		const result = scormMessageTarget();

		const onMessage = (event: MessageEvent) => {
			if (!event.data) return;
			switch (event.data.type) {
				case 'SCORM_GET_LESSON_STATUS_RESPONSE':
					setInitialLessonStatus(event.data.lessonStatus);
					break;
				case 'GET_IS_SCORM_RESPONSE':
					clearHandshakeTimer();
					setIsSCORM(event.data.isSCORM);
					if (event.data.isSCORM && result.status === 'ready') {
						rememberScormHost(result.hasOpener ? 'opener' : 'parent');
					}
					break;
				default:
					break;
			}
		};

		window.addEventListener('message', onMessage);

		if (result.status === 'ready') {
			result.target.postMessage({ type: 'GET_IS_SCORM' }, '*');
			if (result.hasOpener) {
				handshakeTimer = window.setTimeout(() => {
					logClientEvent(CLIENT_EVENT.SCORM_HANDSHAKE_TIMEOUT, { hasOpener: true }, 'warn').catch(() => undefined);
				}, SCORM_HANDSHAKE_TIMEOUT_MS);
			}
		} else {
			reportUnreachableHost('GET_IS_SCORM', result.hasOpener);
		}

		return () => {
			clearHandshakeTimer();
			window.removeEventListener('message', onMessage);
		};
	}, []);

	// if we're in a SCORM env. get the inital lesson status
	useEffect(() => {
		if (isSCORM && initialLessonStatus === null) {
			postToScormHost({ type: 'GET_LESSON_STATUS' });
		}
	}, [isSCORM]);

	return (
		<ScormContext.Provider value={scormContextValue}>
			{completionUnreachable && (
				// Practice pages pin a 56px header at the top. The banner starts below it.
				<div
					role="status"
					className="fixed inset-x-0 top-7 z-50 bg-red-800 px-4 py-3 text-center text-body-medium text-grey-50"
				>
					{SCORM_COMPLETION_UNREACHABLE}
				</div>
			)}
			{children}
		</ScormContext.Provider>
	);
}
